import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useFacility } from "@/lib/facility/facility";
import { orpc, type Output, type orpcClient } from "@/lib/orpc";
import { useCan, useFeature } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { LeadSourceSecretDialog } from "./-components/crm/lead-source-secret-dialog";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { WebsiteLeadSourceForm } from "./-components/crm/website-lead-source-form";
import { WebsiteLeadSourceRow } from "./-components/crm/website-lead-source-row";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/website-lead-sources",
)({
  beforeLoad: ({ context }) => guardRoute(context.me, "crm-website-lead-sources"),
  component: WebsiteLeadSourceSettings,
});

type Source = Output<typeof orpcClient.crm.websiteIntake.sources.list>[number];

const fail = (error: Error) => toast.error(errorWords(error));
const blankToNull = (value: string | null | undefined) => (value?.trim() ? value.trim() : null);

/** The websites allowed to post an enquiry straight into a building's pipeline. */
function WebsiteLeadSourceSettings() {
  const { facilityId } = useFacility();
  // A source belongs to one building, so adding one needs a building chosen.
  const realFacility = facilityId === "all" ? null : facilityId;
  const canManage = useCan("crm:manage");
  // Both halves are read unconditionally: `&&` on two hook calls would skip the
  // second one on the renders where the first is false.
  const canReadFacilities = useCan("facility:read");
  const facilitiesOn = useFeature("facilities");
  const canSeeFacilities = canReadFacilities && facilitiesOn;
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Source | null>(null);
  const [rotating, setRotating] = useState<Source | null>(null);
  // Shown once, straight after a create or a rotate; never recoverable after.
  const [reveal, setReveal] = useState<{ name: string; secret: string } | null>(null);

  const sources = useQuery(
    orpc.crm.websiteIntake.sources.list.queryOptions({ input: { facilityId } }),
  );
  /* The "Text us" half of each source: how many visits became texts, and
     whether that community can receive one at all. */
  const textStats = useQuery(
    orpc.crm.websiteIntake.sources.textStats.queryOptions({ input: { facilityId } }),
  );
  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: true } }),
    enabled: canSeeFacilities,
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.crm.websiteIntake.sources.key() });

  const create = useMutation(
    orpc.crm.websiteIntake.sources.create.mutationOptions({
      onSuccess: async (result) => {
        setAdding(false);
        setReveal({ name: result.source.name, secret: result.secret });
        await invalidate();
      },
      onError: fail,
    }),
  );
  const update = useMutation(
    orpc.crm.websiteIntake.sources.update.mutationOptions({
      onSuccess: async () => {
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  // The confirm closes (and clears `rotating`) on the press, before the key
  // comes back, so the name for the reveal travels with the call instead.
  const rotate = useMutation(
    orpc.crm.websiteIntake.sources.rotateSecret.mutationOptions({
      onSuccess: invalidate,
      onError: fail,
    }),
  );
  const rotateKey = (source: Source) =>
    rotate.mutate(
      { sourceId: source.id },
      { onSuccess: (result) => setReveal({ name: source.name, secret: result.secret }) },
    );

  const buildingName = (id: string) =>
    facilities.data?.find((facility) => facility.id === id)?.name ?? null;

  const rows = (sources.data ?? []).toSorted((a, b) =>
    a.isActive === b.isActive ? a.name.localeCompare(b.name) : a.isActive ? -1 : 1,
  );

  return (
    <>
      <PageHeader
        title="Website lead sources"
        meta="The websites whose contact forms and Text us buttons send enquiries straight into a building's leads."
      />

      {canManage && !realFacility ? (
        <OneBuildingCard
          prompt={"Every building's websites are listed below. To add one, choose its building."}
        />
      ) : null}

      <RecordCard
        title="Websites"
        count={sources.data ? rows.length : undefined}
        canWrite={canManage && Boolean(realFacility)}
        addLabel="Add website"
        onAdd={() => setAdding(true)}
      >
        {sources.isPending ? (
          <RowsSkeleton />
        ) : sources.isError ? (
          <ReadFailed
            message="The websites could not be loaded."
            error={sources.error}
            retrying={sources.isFetching}
            onRetry={() => void sources.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyRows
            text={
              realFacility
                ? "No websites yet. Add one so its contact form sends enquiries straight into Leads."
                : "No websites yet."
            }
          />
        ) : (
          <ul className="divide-y">
            {rows.map((source) => (
              <WebsiteLeadSourceRow
                key={source.id}
                source={source}
                buildingName={buildingName(source.facilityId)}
                canManage={canManage}
                textStats={textStats.data}
                onEdit={() => setEditing(source)}
                onRotate={() => setRotating(source)}
                onToggleActive={() =>
                  update.mutate({ sourceId: source.id, isActive: !source.isActive })
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add website</DialogTitle>
            <DialogDescription>
              Adding it issues a secret key, shown once. Put that key in the website&apos;s contact
              form setup.
            </DialogDescription>
          </DialogHeader>
          <WebsiteLeadSourceForm
            pending={create.isPending}
            submitLabel="Add website"
            onCancel={() => setAdding(false)}
            onSubmit={(values) => {
              if (!realFacility) return;
              create.mutate({
                facilityId: realFacility,
                name: values.name,
                websiteDomain: blankToNull(values.websiteDomain),
              });
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit website</DialogTitle>
          </DialogHeader>
          {editing ? (
            <WebsiteLeadSourceForm
              key={editing.id}
              pending={update.isPending}
              defaultValues={{ name: editing.name, websiteDomain: editing.websiteDomain ?? "" }}
              onCancel={() => setEditing(null)}
              onSubmit={(values) =>
                update.mutate({
                  sourceId: editing.id,
                  name: values.name,
                  websiteDomain: blankToNull(values.websiteDomain),
                })
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <LeadSourceSecretDialog reveal={reveal} onClose={() => setReveal(null)} />

      <AlertDialog open={rotating !== null} onOpenChange={(open) => !open && setRotating(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate the key for “{rotating?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              A new key is issued and shown once. The current one stops working immediately, so the
              website will not be able to post again until it carries the new key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => rotating && rotateKey(rotating)}
              disabled={rotate.isPending}
            >
              {rotate.isPending ? "Rotating…" : "Rotate key"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
